/**
 * Sequence-diagram fragment box (alt / opt / loop / par / critical / break).
 * A semi-transparent bordered rectangle spanning the enclosed participants and
 * rows, with a corner label tab ("alt [condition]") and dashed divider lines at
 * each else/and section. Rendered behind messages (low zIndex via the node).
 */

import { memo } from 'react'
import { NodeProps } from '@xyflow/react'
import type { SequenceFragment } from '../../types/schema'
import { seqRowY } from '../../utils/sequenceLayout'

interface FragmentData {
  label: string
  sequenceFragment?: SequenceFragment
}

function SequenceFragmentNode({ data, width, height, positionAbsoluteY }: NodeProps) {
  const nodeData = data as unknown as FragmentData
  const f = nodeData.sequenceFragment
  if (!f) return null
  const w = typeof width === 'number' ? width : 200
  const h = typeof height === 'number' ? height : 120
  // section divider y is in diagram coords (seqRowY); convert to node-local.
  const baseY = typeof positionAbsoluteY === 'number' ? positionAbsoluteY : 0

  return (
    <div
      style={{
        width: w, height: h, position: 'relative',
        border: '1.5px solid #cbd5e1', borderRadius: 6,
        background: 'rgba(148,163,184,0.06)', boxSizing: 'border-box',
        fontFamily: 'Inter, system-ui, sans-serif', pointerEvents: 'none',
      }}
    >
      {/* corner label tab */}
      <div
        style={{
          position: 'absolute', top: 0, left: 0,
          background: '#e2e8f0', color: '#334155', fontSize: 11, fontWeight: 700,
          padding: '2px 10px 3px', borderRight: '1.5px solid #cbd5e1',
          borderBottom: '1.5px solid #cbd5e1', borderBottomRightRadius: 8,
        }}
      >
        {f.kind}{f.label ? ` [${f.label}]` : ''}
      </div>

      {/* else / and dividers */}
      {(f.sections ?? []).map((s, i) => {
        const top = seqRowY(s.atRow) - baseY
        if (top < 0 || top > h) return null
        return (
          <div key={i}>
            <div style={{ position: 'absolute', left: 0, right: 0, top, borderTop: '1.5px dashed #cbd5e1' }} />
            {s.label && (
              <div style={{ position: 'absolute', left: 8, top: top + 2, fontSize: 10, fontStyle: 'italic', color: '#64748b' }}>
                [{s.label}]
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default memo(SequenceFragmentNode)
